import type { Invitation } from './invitation';

export const PRIMARY_PROVIDER_ID = '00000000-0000-0000-0000-000000000000';

export interface SubscriptionPlan {
  subscriptionId: string;
  amount: string;
}

export interface License {
  userId: string;
  givenName: string;
  familyName: string;
  email: string;
  addedAt: string;
  isActive: boolean;
  deactivatedAt?: string;
}

export interface AccountSubscription {
  monthly: SubscriptionPlan;
  annually: SubscriptionPlan;
  licenses: License[];
  everywareSubscriptionId?: string;
  lastPaymentFailedAt?: string;
  lastPaymentFailureReason?: string;
  consecutivePaymentFailures?: number;
  status?: string;
  suspendedAt?: string;
  suspensionReason?: string;
}

export interface IntegrationConfig {
  type: string;
  name: string;
  enabled: boolean;
  configuration: Record<string, string>;
}

export interface IntegrationRequestDto {
  requestId: string;
  accountId: string;
  integrationType: string;
  requestedBy: string;
  requestDate: string;
  status: string;
}

export interface Account {
  id: string;
  type: AccountType;
  status: AccountStatus;
  companyName: string;
  tagLine?: string;
  overview?: string;
  companyType: string;
  /**
   * The firm's timezone, as an IANA id. Undefined means the platform default, which is what every
   * account that existed before the field reads as - nothing was backfilled.
   */
  timeZoneId?: string;
  numberOfEmployees?: string;
  logoUrl?: string;
  bannerUrl?: string;
  primaryDiscipline?: string;
  primaryDisciplineSkills?: string[];
  yearsOfExperience?: YearsOfExperience;
  categoryExperiences?: ExpertCategoryExperience[];
  industryExpertise?: string[];
  companyRevenue?: string;
  businessClassification?: string[];
  persona?: string;
  typesOfCostEstimates?: string[];
  estimatingExpertise?: string;
  certifiedEstimators?: string[];
  serviceLocations?: string[];
  website?: string;
  companyAddress?: Address;
  clients?: Client[] | null;
  offices?: Office[] | null;
  invitations?: Invitation[] | null;
  users?: User[] | null;
  owner?: string;
  estimateApps?: EstimateApp[];
  providerAccountIds?: string[];
  inactiveProviderAccountIds?: string[];
  inactiveClientAccountIds?: string[];
  providerFee?: {
    phaseFee?: number;
    phaseMin?: number;
    phaseMax?: number;
  };
  quickBooksCustomerId?: string;
  everywareCustomerId?: string;
  clientAccountIds?: string[];
  createdAt?: string;
  updatedAt?: string;
  counts?: {
    [key: string]: number | { id: string; count: number }[];
    clients: number;
    clientProjects: { id: string; count: number }[];
    offices: number;
    officeProjects: { id: string; count: number }[];
    projects: number;
    userTotal: number;
  };
  subscription?: AccountSubscription;
  estimatingSoftware?: string[];
  integrations?: IntegrationConfig[];
  isInternalClient?: boolean;
}

export type EstimateApp = {
  type: string;
};

export type AccountUser = ResourceUser & {
  invitationId?: string;
  invitationStatus?: string;
  invitationStatusAt?: string;
  invitationCreatedAt?: string;
};

export enum AccountType {
  Provider = 'Provider',
  Client = 'Client',
  Expert = 'Expert',
}

export enum AccountStatus {
  New = 'New',
  Ready = 'Ready',
  Vetted = 'Vetted',
  Canceling = 'Canceling',
  Closed = 'Closed',
  Deferred = 'Deferred',
}

export enum ResourceUserRole {
  Owner = 'Owner',
  Administrator = 'Administrator',
  Contributor = 'Contributor',
  Guest = 'Guest',
}

export enum SystemUserRole {
  Administration = 'Administration',
  Support = 'Support',
}

export interface ResourceUser {
  id: string;
  role: ResourceUserRole;
  status: string;
  givenName: string;
  familyName: string;
  systemRole?: SystemUserRole;
  isProvider?: boolean;
  isClient?: boolean;
  /** On the account only to reach named project resources; not a member of the company. */
  isProjectScoped?: boolean;
  /** Invited to one project from outside the firm doing the work. */
  isExternal?: boolean;
  /** Whether that collaborator may see the money on this project. Absent and false both mean no. */
  canSeeFees?: boolean;
  providerPosition?: string;
}

export enum UserStatus {
  Ready = 'Ready',
  Unavailable = 'Unavailable',
}

export interface User extends ResourceUser {
  status: UserStatus;
  givenName: string;
  familyName: string;
  jobTitle?: string;
  theme?: string;
  emails?: EmailAddress[];
  pictureUrl?: string;
  businessPhones?: PhoneNumber[];
  mobilePhone?: string;

  providerId?: string;
  createUserType?: string | null;
  hourlyRate?: number;
  primaryDiscipline?: string;
  primaryDisciplineSkills?: string[];
  yearsOfExperience?: YearsOfExperience;
  availability?: Availability;
  expertRole?: ExpertRole;
  expertType?: ExpertType;
  categoryExperiences?: ExpertCategoryExperience[];
  educationLevel?: EducationLevel;
  schoolName?: string;
  gradYear?: number;
  industrySpecificDegree?: boolean;
  certifications?: ExpertCertification[];

  personalAddress?: Address;
  distance?: number;

  invitations?: Invitation[];
  createdAt?: string;
  welcomeAt?: string;
  onboardedAt?: string;
  onboardAccountId?: string;

  accountSettings?: UserAccountSetting[];

  color?: string;
}

export interface UserAccountSetting {
  accountId: string;
  invitedByProviderId?: string;
  hourlyRate?: number;
  jobTitle?: string;
  availability?: Availability;
  expertRole?: ExpertRole;
  expertType?: ExpertType;
}

export interface Address {
  [key: string]: any;
  label?: string;
  type?: string;
  buildingNumber?: string;
  country?: string;
  countryCode?: string;
  countrySubdivision?: string;
  countrySubdivisionName?: string;
  countrySecondarySubdivision?: string;
  countryTertiarySubdivision?: string;
  crossStreet?: string;
  extendedPostalCode?: string;
  freeformAddress?: string;
  localName?: string;
  municipality?: string;
  municipalitySubdivision?: string;
  postalCode?: string;
  street?: string;
  streetName?: string;
  streetNameAndNumber?: string;
  streetNumber?: string;
  lat?: number;
  lon?: number;
}

export interface EmailAddress {
  label?: string;
  email: string;
}
export interface PhoneNumber {
  label?: string;
  number: string;
  extension?: string;
}

export interface Client {
  id: string;
  name: string;
  contactName?: string;
  addresses: Address[];
  emailAddresses?: EmailAddress[] | null;
  phoneNumbers?: PhoneNumber[] | null;
  users?: ResourceUser[] | null;
}
export interface LaborRate {
  id: string;
  accountId: string;
  discipline: number;
  customName?: string;
  rate: number;
  isActive?: boolean;
  /** Whether this discipline seeds a fee worksheet. A rate kept for costing need not appear on one. */
  showOnEstimates?: boolean;
  history?: LaborRateHistory[];
  createdAt: string;
  createdById: string;
  updatedAt: string;
  updatedById: string;
  links?: Record<string, string>;
}
/** One entry in a firm's list of the direct costs it bills. A name, and whether it is still offered. */
export interface OdcItem {
  id: string;
  accountId?: string;
  name: string;
  isActive?: boolean;
  links?: Record<string, string>;
}

export interface LaborRateHistory {
  rate: number;
  createdAt: string;
  createdById: string;
  action?: string;
  customName?: string;
  isActive?: boolean;
  links?: Record<string, string>;
}

export interface Office {
  id: string;
  name: string;
  address?: Address;
  phoneNumbers?: PhoneNumber[] | null;
  users?: User[] | null;
  resourceUsers?: ResourceUser[] | null;
  counts?: {
    [key: string]: number;
  };
  isActive: boolean;
  links?: {
    [key: string]: string;
  };
}

export interface CompanyType {
  id: string;
  label: string;
}

export interface NumberOfEmployees {
  id: string;
  label: string;
}

export interface ResourceWithUsers {
  id: string;
  users: User[];
}
export interface ProjectWithUsers extends ResourceWithUsers {
  createdBy?: User;
  updatedBy?: User;
  officeId?: string;
  phases: PhaseWithUsers[];
}
export interface PhaseWithUsers extends ResourceWithUsers {
  estimates: ResourceWithUsers[];
}

export interface AccountWithUsers extends ResourceWithUsers {
  type: AccountType;
  invitations: Invitation[];
  offices: ResourceWithUsers[];
  clients: ResourceWithUsers[];
  projects: ProjectWithUsers[];
}

export enum ExpertRole {
  CostEngineer = 'CostEngineer',
  CostEstimator = 'CostEstimator',
  QuantitySurveyor = 'QuantitySurveyor',
}

export enum ExpertType {
  FTE = 'FTE',
  Contractor = 'Contractor',
}

export enum YearsOfExperience {
  Lt2 = 'Lt2',
  In2to5 = 'In2to5',
  In5to10 = 'In5to10',
  In10to15 = 'In10to15',
  In15to20 = 'In15to20',
  In20to25 = 'In20to25',
  Gt25 = 'Gt25',
}

export enum Availability {
  FullTime = 'FullTime',
  PartTime = 'PartTime',
  Contractor = 'Contractor',
}

export enum EducationLevel {
  Associate = 'Associate',
  Bachelor = 'Bachelor',
  Graduate = 'Graduate',
  Doctoral = 'Doctoral',
}

export interface ExpertCategoryExperienceItem {
  projectTypeId: string;
  type: string;
}
export interface ExpertCategoryExperience {
  category: number;
  items: ExpertCategoryExperienceItem[];
}

export interface ExpertCertification {
  name: string;
  number?: string;
  expires?: string;
}

export interface AllProvidersTransactionAggregateDto {
  totalTransactionCount: number;
  totalCreditAmount: number;
  totalDebitAmount: number;
  filterYear?: number;
  filterMonth?: number;
}

export type IPGeolocationResponse = {
  city?: IPCity;
  continent?: IPContinent;
  country: IPCountry;
  location?: IPLocation;
  subdivisions?: IPSubdivision[];
  state?: IPState;
  datasource?: IPDatasource[];
  ip?: string;
};
export type IPNames = {
  [key: string]: string;
};
export type IPLanguage = {
  iso_code: string;
  name: string;
  name_native: string;
};
export type IPCity = {
  name: string;
  names: IPNames;
};
export type IPContinent = {
  code: string;
  geoname_id: number;
  names: IPNames;
  name: string;
};
export type IPCountry = {
  geoname_id: number;
  iso_code: string;
  names: IPNames;
  name: string;
  name_native: string;
  phone_code: string;
  capital: string;
  currency: string;
  flag: string;
  languages: IPLanguage[];
};
export type IPLocation = {
  latitude: number;
  longitude: number;
};
export type IPSubdivision = {
  names: IPNames;
};
export type IPState = {
  name: string;
};
export type IPDatasource = {
  name: string;
  attribution: string;
  license: string;
};

/** Where a relationship is in its life. Mirrors the server's constants. */
export type RelationshipStatus = 'pending' | 'active' | 'inactive';

/**
 * What one account is to another, from one account's point of view.
 *
 * Two of these exist per relationship, mirrored: the direction is on the record rather than
 * inferred from the account's type, which is what lets a client account be someone's provider.
 */
export interface AccountRelationship {
  id: string;
  accountId: string;
  counterpartyAccountId: string;
  party: 'provider' | 'client';
  category?: string | null;
  /** What the customer calls this kind of firm, when the category is 'other'. */
  categoryLabel?: string | null;
  officeId?: string | null;
  status: RelationshipStatus;
}

/**
 * What a firm does for this account.
 *
 * Four, in the order they are offered. The first three are what the platform can
 * reason about; `other` carries whatever the customer would have written on the
 * contract, in `categoryLabel` beside it, and is shown back rather than
 * interpreted.
 */
export const FIRM_TYPES = [
  { id: 'architect-designer-engineer', label: 'Architect/Designer/Engineer' },
  { id: 'pm-cm-firm', label: 'PM/CM Firm' },
  { id: 'estimating-firm', label: 'Estimating Firm' },
  { id: 'other', label: 'Other' },
] as const;

export type FirmType = (typeof FIRM_TYPES)[number]['id'];

/** Categories written before the four above existed, and the one each stands for. */
const LEGACY_FIRM_TYPES: Record<string, FirmType> = {
  'estimating-provider': 'estimating-firm',
  'design-engineering': 'architect-designer-engineer',
};

export const firmTypeOf = (category?: string | null): FirmType => {
  if (!category) return 'other';
  const legacy = LEGACY_FIRM_TYPES[category];
  if (legacy) return legacy;
  return (FIRM_TYPES.find((f) => f.id === category)?.id ?? 'other') as FirmType;
};
